import { routes } from "@/router";
import { ColumnDef } from "@tanstack/react-table";
import { TFunction } from "i18next";
import {
  HiOutlineArrowPath,
  HiOutlineCalendarDays,
  HiOutlineListBullet,
  HiOutlineQueueList,
  HiOutlineSquare3Stack3D,
} from "react-icons/hi2";
import { Link } from "react-router-dom";

import { ListType } from "@/types/List.type";
import { HeaderDictionary } from "@/types/Table.type";
import Badge from "@/components/atoms/Badge";
import Checkbox from "@/components/atoms/Checkbox";
import CompanyLogoStack from "@/components/molecules/company/CompanyLogoStack";

export const basicListColumns = (t: TFunction): HeaderDictionary => ({
  name: {
    id: "name",
    name: t("static.table.name"),
  },
  type: {
    id: "type",
    name: t("static.table.type"),
  },
  item_count: {
    id: "item_count",
    name: t("static.table.entries"),
  },
  date: {
    id: "date",
    name: t("static.table.date"),
  },
});

export const basicListColumnsDef = (t: TFunction): ColumnDef<ListType>[] => [
  {
    accessorFn: (row) => row.name,
    id: basicListColumns(t).name.id,
    header: ({ table }) => (
      <>
        <Checkbox
          {...{
            checked: table.getIsAllRowsSelected(),
            indeterminate: table.getIsSomeRowsSelected(),
            onChange: table.getToggleAllRowsSelectedHandler(),
          }}
        />
        <HiOutlineQueueList className={"ml-2 size-4 stroke-2 text-quaternary"} />
        {basicListColumns(t).name.name}
      </>
    ),
    cell: ({ row, getValue }) => (
      <div className="flex w-full items-center gap-2">
        <Link to={`${routes.lists}/${row.original.id}`} className={"flex w-full items-center space-x-2"}>
          <CompanyLogoStack
            spacing={"sm"}
            imageUrls={row.original.preview_items.map((item) => item.logo_url)}
            className={"w-14 shrink-0 justify-end"}
          />

          <span className={"text-primary"}>{getValue<string>()}</span>
        </Link>
      </div>
    ),
    footer: (props) => props.column.id,
    meta: {
      disableReorder: true,
    },
  },
  {
    accessorKey: "type",
    id: basicListColumns(t).type.id,
    header: basicListColumns(t).type.name,
    cell: ({ row }) => {
      if (row.original.type === "STATIC") {
        return (
          <Badge color={"natural"} size={"sm"} leadingIcon={<HiOutlineListBullet />} label={t("static.types.static")} />
        );
      }

      return (
        <Badge color={"primary"} size={"sm"} leadingIcon={<HiOutlineArrowPath />} label={t("static.types.dynamic")} />
      );
    },
    meta: {
      heading: {
        icon: HiOutlineListBullet,
      },
    },
  },
  {
    accessorKey: "item_count",
    id: basicListColumns(t).item_count.id,
    header: basicListColumns(t).item_count.name,
    cell: (info) => info.getValue(),
    meta: {
      heading: {
        icon: HiOutlineSquare3Stack3D,
      },
    },
  },
  {
    accessorKey: "date",
    id: basicListColumns(t).date.id,
    header: basicListColumns(t).date.name,
    cell: (info) => new Date(info.getValue<string>()).toLocaleDateString(),
    meta: {
      heading: {
        icon: HiOutlineCalendarDays,
      },
    },
  },
];
